import { useEffect, useState } from "react";
import { GridMetaData, createUrlSearchParams } from "models/grids";
import { useCurrentUser, useCentralApi } from "hooks/shared";
import { IFileAttachment } from "models/attachments";

type GetWorkItemAttachmentsResult = {
    attachments: IFileAttachment[],
    total: number
};

type WorkItemAttachmentsResult = [
    IFileAttachment[],
    number,
    boolean,
    string | null
];

const makeWorkItemAttachmentsUrl = (workItemId: number, query: string, draft?: boolean) => draft 
    ? `/api/v3/drafts/workitems/${workItemId}/attachments?${query}`
    : `/api/v3/workitems/${workItemId}/attachments?${query}`;

const useWorkItemAttachments: (gridMetadata: GridMetaData, dependenciesLoaded: boolean, workItemId?: number, all?: boolean, updateFromDelete?: boolean, updateFromAdd?: boolean, draft?: boolean) => 
    WorkItemAttachmentsResult = (gridMetadata, dependenciesLoaded, workItemId = 0, all = true, updateFromDelete = undefined, updateFromAdd = undefined, draft?: boolean) => {
    const [ _lastQuery, _setLastQuery ] = useState<string>('');
    const [ result, setResult ] = useState<IFileAttachment[]>([]);
    const [ total, setTotal ] = useState<number>(0);
    const [ error, setError ] = useState<string | null>(null);
    const [ loading, setLoading ] = useState<boolean>(true);    
    const user = useCurrentUser();
    const fetcher = useCentralApi(user);

    useEffect(() => {        
        const searchParams = createUrlSearchParams(gridMetadata);
        searchParams.append('all', all.toString());
        const query = `${searchParams}`;

        const controller = new AbortController();

        const fetchWorkItemAttachments = async () => {
            if ((user && user.id_token && dependenciesLoaded) || (updateFromDelete && dependenciesLoaded) || (updateFromAdd && dependenciesLoaded)) {                
                setLoading(true);
                try {
                    const urlRoot = makeWorkItemAttachmentsUrl(workItemId, query, draft);
                    const resp = await fetcher( urlRoot , {
                        signal: controller.signal
                    });

                    if (!resp.ok) {
                        // RESPOND TO ERRORS
                    }

                    const data = await resp.json() as GetWorkItemAttachmentsResult;
                    setResult(data.attachments);

                    if (data.total !== total) {
                        setTotal(data.total);
                    }
                    _setLastQuery(query);
                } catch (err) {
                    // Will be done in BFLY-8658 or BFLY-9022
                } finally {
                    if (!controller.signal.aborted)
                        setLoading(false);
                }
            }
        };
        
        fetchWorkItemAttachments();
        return () => {
            controller.abort();
            setLoading(false);
        }
    }, [ dependenciesLoaded, gridMetadata, user, workItemId, all, updateFromDelete, updateFromAdd ]);

    return [
        result,
        total,
        loading,
        error
    ];
};

export default useWorkItemAttachments;